import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, uploadImageFiles } from "../lib/api";
import { useStores } from "../lib/stores";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, X } from "lucide-react";
import ImageUploader from "../components/ImageUploader";
import { imageUrl } from "../lib/api";

const ALL_SIZES = ["Free Size", "XS", "S", "M", "L", "XL", "XXL", "XXXL"];
const MAX_VARIANT_IMAGES = 5;

// Fixed columns: Size | Color | Price | GST | Qty | Barcode | Images (5 thumbs) | Delete
const VARIANT_ROW_GRID =
  "grid grid-cols-[6.25rem_5.5rem_5rem_3.5rem_3rem_8.75rem_18.75rem_2.25rem] items-center gap-x-2";
const VARIANT_TABLE_MIN_W = "min-w-[61rem]";

function newRowKey() {
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function normSize(size) {
  if (!size || size === "Free") return "Free Size";
  return size;
}

function emptyVariantRow(overrides = {}) {
  return {
    rowKey: newRowKey(),
    id: undefined,
    size: "Free Size",
    color: "",
    price: "",
    gst_percent: "",
    stock: 1,
    barcode: "",
    images: [],
    sold: false,
    ...overrides,
  };
}

function rowHasData(row) {
  if (!row) return false;
  return Boolean(
    (row.price !== "" && row.price != null)
    || row.barcode?.trim()
    || (row.images && row.images.length > 0),
  );
}

function rowToPayload(row) {
  return {
    id: row.id,
    size: row.size,
    color: row.color?.trim() || "",
    price: Number(row.price || 0),
    gst_percent: row.gst_percent === "" || row.gst_percent == null ? 5 : Number(row.gst_percent),
    cost_price: 0,
    stock: Number(row.stock || 1),
    barcode: row.barcode?.trim() || undefined,
    images: row.images || [],
  };
}

function serializeFormState(base, templateImages, variants) {
  return JSON.stringify({
    base,
    templateImages,
    variants: variants.map(({ id, size, color, price, gst_percent, stock, barcode, images }) => ({
      id,
      size,
      color,
      price,
      gst_percent,
      stock,
      barcode,
      images,
    })),
  });
}

export default function ProductVariantsForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { activeStoreId } = useStores();
  const [base, setBase] = useState({ name: "", catalogue_no: "", hsn_code: "", category: "Kurta", fabric: "", shelf_location: "Rack-A", low_stock_threshold: 2 });
  const [templateImages, setTemplateImages] = useState([]);
  const [cats, setCats] = useState([]);
  const [variants, setVariants] = useState([emptyVariantRow({ color: "Red" })]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [catsReady, setCatsReady] = useState(isEdit);
  const [groupMeta, setGroupMeta] = useState({ template_id: null, seed_id: null, status: "in_shelf" });
  const baselineRef = useRef(null);
  const baselineSetRef = useRef(false);

  const isFormDirty = () => {
    if (!baselineRef.current) return false;
    return serializeFormState(base, templateImages, variants) !== baselineRef.current;
  };

  const handleLeave = (e) => {
    e?.preventDefault();
    if (isFormDirty() && !window.confirm("You have unsaved changes. Leave without saving?")) return;
    navigate("/app/products");
  };

  useEffect(() => {
    api.get("/categories").then((r) => {
      setCats(r.data);
      if (!isEdit && r.data.length) setBase((b) => ({ ...b, category: r.data[0].name }));
    }).finally(() => {
      if (!isEdit) setCatsReady(true);
    });
  }, [isEdit]);

  useEffect(() => {
    if (baselineSetRef.current) return;
    if (isEdit) {
      if (loading) return;
    } else if (!catsReady) {
      return;
    }
    baselineSetRef.current = true;
    baselineRef.current = serializeFormState(base, templateImages, variants);
  }, [isEdit, loading, catsReady, base, templateImages, variants]);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    api
      .get(`/products/${id}/group`)
      .then((r) => {
        const { base: b, variants: loaded, template_images, template_id, seed_id } = r.data;
        setBase({
          name: b.name || "",
          catalogue_no: b.catalogue_no || "",
          hsn_code: b.hsn_code || "",
          category: b.category || "Kurta",
          fabric: b.fabric || "",
          shelf_location: b.shelf_location || "Rack-A",
          low_stock_threshold: b.low_stock_threshold ?? 2,
        });
        setTemplateImages(template_images || []);
        setGroupMeta({ template_id, seed_id, status: b.status || "in_shelf" });

        const rows = loaded.map((v) => ({
          rowKey: v.id || newRowKey(),
          id: v.id,
          size: normSize(v.size),
          color: v.color || "",
          price: v.price ?? "",
          gst_percent: v.gst_percent ?? "",
          stock: v.stock ?? 1,
          barcode: v.barcode || "",
          images: v.images || [],
          sold: v.status === "sold",
        }));
        setVariants(rows.length ? rows : [emptyVariantRow()]);
      })
      .catch(() => {
        toast.error("Product not found");
        navigate("/app/products");
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const updateVariant = (index, patch) => {
    setVariants((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addVariantRow = () => {
    setVariants((prev) => [...prev, emptyVariantRow()]);
  };

  const removeVariantRow = async (index) => {
    const row = variants[index];
    if (variants.length <= 1) return toast.error("Keep at least one variant row");
    if (row.sold) return toast.error("Cannot remove a sold variant");

    if (!window.confirm("Remove this variant?")) return;

    if (row.id) {
      try {
        await api.delete(`/products/${row.id}`);
      } catch (err) {
        return toast.error(err?.response?.data?.detail || "Failed to remove variant");
      }
    }

    const nextVariants = variants.filter((_, i) => i !== index);
    setVariants(nextVariants);
    baselineRef.current = serializeFormState(base, templateImages, nextVariants);
    toast.success("Variant removed");
  };

  const buildVariants = (asDraft) => {
    const out = [];
    variants.forEach((row) => {
      if (asDraft) {
        if (!rowHasData(row)) return;
        out.push(rowToPayload(row));
      } else if (Number(row.price) > 0) {
        out.push(rowToPayload(row));
      }
    });
    if (asDraft && out.length === 0 && variants.length) {
      out.push(rowToPayload({ ...variants[0], price: 0 }));
    }
    return out;
  };

  const submit = async (e, { asDraft = false, publish = false } = {}) => {
    e?.preventDefault();
    if (!base.name.trim()) return toast.error("Product name is required");
    const payloadVariants = buildVariants(asDraft);
    if (!asDraft && payloadVariants.length === 0) return toast.error("Enter price for at least one variant");
    setBusy(true);
    const status = publish ? "in_shelf" : asDraft ? "draft" : isEdit ? groupMeta.status : "in_shelf";
    const payload = {
      ...base,
      low_stock_threshold: Number(base.low_stock_threshold || 2),
      store_id: activeStoreId || undefined,
      template_images: templateImages,
      status,
      variants: payloadVariants,
    };
    try {
      if (isEdit) {
        await api.put("/products/bulk-variants", {
          ...payload,
          template_id: groupMeta.template_id,
          seed_id: groupMeta.seed_id || id,
        });
        toast.success(publish ? "Product published" : asDraft ? `Draft saved (${payloadVariants.length} variants)` : `Updated ${payloadVariants.length} variants`);
      } else {
        const { data } = await api.post("/products/bulk-variants", payload);
        toast.success(asDraft ? `Draft saved (${data.count} variants)` : `Created ${data.count} variants with unique barcodes`);
      }
      navigate("/app/products");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="product-variants-form">
      <Link
        to="/app/products"
        onClick={handleLeave}
        className="inline-flex items-center gap-2 text-sm font-medium mb-4"
        style={{ color: "var(--text-muted)" }}
        data-testid="product-variants-back"
      >
        <ArrowLeft size={16} /> Back to products
      </Link>

      <h1 className="font-display font-bold text-3xl mb-1">{isEdit ? "Edit product" : "Add product"}</h1>
      <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>
        {isEdit
          ? groupMeta.status === "draft"
            ? "Draft — update variants below. Not visible at POS until published."
            : "Update variant rows below. Sold variants cannot be removed."
          : "Add one row per size + color you stock. Save as draft to finish later — drafts are hidden from POS until published."}
      </p>

      {isEdit && groupMeta.status === "draft" && (
        <div className="mb-4 px-4 py-3 rounded-md text-sm" style={{ background: "var(--surface-alert)", color: "var(--warning)" }}>
          Draft — not available for sale at POS
        </div>
      )}

      <form onSubmit={(e) => submit(e)} className="card-soft p-6">
        <div className="grid md:grid-cols-3 gap-3 mb-5">
          <Field label="Product name*" required v={base.name} onChange={(v) => setBase({ ...base, name: v })} testid="vm-name" />
          <div>
            <label className="text-sm font-medium">Category</label>
            <select data-testid="vm-category" value={base.category} onChange={(e) => setBase({ ...base, category: e.target.value })} className="mt-1 w-full h-11 px-3 rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }}>
              {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <Field label="Fabric" v={base.fabric} onChange={(v) => setBase({ ...base, fabric: v })} testid="vm-fabric" />
        </div>

        <div className="grid md:grid-cols-3 gap-3 mb-5">
          <Field
            label="Catalogue Name / No"
            v={base.catalogue_no}
            onChange={(v) => setBase({ ...base, catalogue_no: v })}
            testid="vm-catalogue-no"
          />
          <Field
            label="HSN Code"
            v={base.hsn_code}
            onChange={(v) => setBase({ ...base, hsn_code: v.replace(/\s/g, "") })}
            testid="vm-hsn"
            hint="e.g. 6204 for apparel"
          />
          <Field label="Shelf" v={base.shelf_location} onChange={(v) => setBase({ ...base, shelf_location: v })} testid="vm-shelf" />
        </div>

        <div className="mb-6">
          <ImageUploader
            row
            images={templateImages}
            onChange={setTemplateImages}
            max={5}
            label="Product-level images"
            hint="Shared images for this product line (all variants) — up to 5, 1 MB each, auto-resized to 500px."
          />
        </div>

        <div className="overflow-x-auto border rounded-lg mb-4" style={{ borderColor: "var(--border-soft)" }}>
          <div className={VARIANT_TABLE_MIN_W}>
            <div
              className={`${VARIANT_ROW_GRID} px-3 py-2 text-[10px] font-semibold uppercase tracking-wide border-b`}
              style={{ borderColor: "var(--border-soft)", background: "var(--surface-tint)", color: "var(--text-muted)" }}
            >
              <span>Size</span>
              <span>Color</span>
              <span>Price (₹)</span>
              <span>GST (%)</span>
              <span>Qty</span>
              <span>Barcode</span>
              <span>Images</span>
              <span className="sr-only">Remove</span>
            </div>

            {variants.map((row, index) => (
              <div
                key={row.rowKey}
                className={`${VARIANT_ROW_GRID} px-3 py-3 border-b last:border-b-0`}
                style={{ borderColor: "var(--border-soft)" }}
              >
                <select
                  data-testid={`vm-variant-${index}-size`}
                  value={row.size}
                  disabled={row.sold}
                  onChange={(e) => updateVariant(index, { size: e.target.value })}
                  className="h-9 w-full px-2 border rounded text-sm bg-white disabled:opacity-60"
                  style={{ borderColor: "var(--border-soft)" }}
                >
                  {ALL_SIZES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>

                <input
                  data-testid={`vm-variant-${index}-color`}
                  value={row.color}
                  disabled={row.sold}
                  placeholder="Color"
                  onChange={(e) => updateVariant(index, { color: e.target.value })}
                  className="h-9 w-full px-2 border rounded text-sm bg-white disabled:opacity-60"
                  style={{ borderColor: "var(--border-soft)" }}
                />

                <input
                  data-testid={`vm-variant-${index}-price`}
                  type="number"
                  value={row.price}
                  disabled={row.sold}
                  onChange={(e) => updateVariant(index, { price: e.target.value })}
                  className="h-9 w-full px-2 border rounded text-sm num bg-white disabled:opacity-60"
                  style={{ borderColor: "var(--border-soft)" }}
                />

                <input
                  data-testid={`vm-variant-${index}-gst`}
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={row.gst_percent}
                  disabled={row.sold}
                  onChange={(e) => updateVariant(index, { gst_percent: e.target.value })}
                  className="h-9 w-full px-2 border rounded text-sm num bg-white disabled:opacity-60"
                  style={{ borderColor: "var(--border-soft)" }}
                />

                <input
                  data-testid={`vm-variant-${index}-qty`}
                  type="number"
                  value={row.stock}
                  disabled={row.sold}
                  onChange={(e) => updateVariant(index, { stock: e.target.value })}
                  className="h-9 w-full px-2 border rounded text-sm num bg-white disabled:opacity-60"
                  style={{ borderColor: "var(--border-soft)" }}
                />

                <input
                  data-testid={`vm-variant-${index}-barcode`}
                  placeholder={row.sold ? "Locked" : ""}
                  value={row.barcode}
                  readOnly={row.sold}
                  onChange={(e) => updateVariant(index, { barcode: e.target.value })}
                  className="h-9 w-full px-2 border rounded text-xs font-mono bg-white disabled:opacity-60"
                  style={{ borderColor: "var(--border-soft)", background: row.sold ? "#f9f9f9" : "white" }}
                />

                <div className="min-w-0 w-full max-w-[18.75rem] overflow-hidden">
                  {row.sold ? (
                    <span className="text-[10px] font-semibold uppercase" style={{ color: "var(--error)" }}>Sold</span>
                  ) : (
                    <VariantCellImages
                      inline
                      images={row.images || []}
                      onChange={(images) => updateVariant(index, { images })}
                    />
                  )}
                </div>

                <div className="flex justify-center">
                  {variants.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => removeVariantRow(index)}
                      disabled={row.sold}
                      className="w-9 h-9 rounded-md inline-flex items-center justify-center hover:bg-red-50 disabled:opacity-40"
                      style={{ color: "var(--error)" }}
                      data-testid={`vm-variant-${index}-remove`}
                      title="Remove variant"
                    >
                      <Trash2 size={16} />
                    </button>
                  ) : (
                    <span className="w-9 h-9" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={addVariantRow}
          data-testid="vm-add-variant"
          className="mb-6 h-10 px-4 rounded-md border text-sm font-semibold inline-flex items-center gap-2"
          style={{ borderColor: "var(--border-soft)" }}
        >
          <Plus size={16} /> Add variant
        </button>

        <div className="flex flex-wrap gap-3 pt-4 border-t" style={{ borderColor: "var(--border-soft)" }}>
          <button
            type="button"
            onClick={handleLeave}
            data-testid="vm-cancel"
            className="h-11 px-5 rounded-md border inline-flex items-center"
            style={{ borderColor: "var(--border-soft)" }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            data-testid="vm-save-draft"
            onClick={(e) => submit(e, { asDraft: true })}
            className="h-11 px-5 rounded-md border font-semibold disabled:opacity-60"
            style={{ borderColor: "var(--border-soft)" }}
          >
            {busy ? "Saving..." : "Save as draft"}
          </button>
          {isEdit && groupMeta.status === "draft" && (
            <button
              type="button"
              disabled={busy}
              data-testid="vm-publish"
              onClick={(e) => submit(e, { publish: true })}
              className="h-11 px-6 rounded-md text-white tactile-btn font-semibold disabled:opacity-60"
              style={{ background: "var(--secondary)" }}
            >
              {busy ? "Publishing..." : "Publish for sale"}
            </button>
          )}
          <button
            disabled={busy}
            data-testid="vm-save"
            type="submit"
            className="h-11 px-6 rounded-md text-white font-semibold tactile-btn inline-flex items-center gap-2 disabled:opacity-60"
            style={{ background: "var(--primary)" }}
          >
            <Plus size={16} /> {busy ? "Saving..." : isEdit ? "Save changes" : "Create variants & generate barcodes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, v, onChange, required, testid, hint }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <input data-testid={testid} required={required} value={v} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-md border bg-white" style={{ borderColor: "var(--border-soft)" }} />
      {hint && <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{hint}</p>}
    </div>
  );
}

function VariantCellImages({ images, onChange, max = MAX_VARIANT_IMAGES, inline = false }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const remaining = max - images.length;

  const uploadFiles = async (fileList) => {
    if (!fileList?.length || remaining <= 0) return;
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/")).slice(0, remaining);
    if (!files.length) return toast.error("Please choose image files only");

    setUploading(true);
    try {
      const urls = await uploadImageFiles(files, remaining);
      onChange([...images, ...urls]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="w-full min-w-0">
      {!inline && (
        <div className="text-xs font-medium mb-2" style={{ color: "var(--text-muted)" }}>
          Images ({images.length}/{max})
        </div>
      )}
      <div className="flex flex-wrap gap-1.5 max-w-[18.75rem]">
        {images.map((src, i) => (
          <div key={`${src}-${i}`} className="relative w-11 h-11 rounded-md border overflow-hidden shrink-0" style={{ borderColor: "var(--border-soft)" }}>
            <img src={imageUrl(src)} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange(images.filter((_, idx) => idx !== i))}
              className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-white/90 text-gray-700 grid place-items-center shadow-sm border"
              style={{ borderColor: "var(--border-soft)" }}
            >
              <X size={9} />
            </button>
          </div>
        ))}
        {remaining > 0 && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-11 h-11 rounded-md border-2 border-dashed flex flex-col items-center justify-center shrink-0"
            style={{ borderColor: "var(--primary)", color: "var(--primary)" }}
          >
            <Plus size={14} />
            {!inline && <span className="text-[10px] font-medium">{uploading ? "..." : "Add"}</span>}
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} />
    </div>
  );
}
